import {
  Headphones, Users, TrendingUp, Megaphone,
  Landmark, Shield, Heart, Truck, ShoppingBag, Scale,
} from 'lucide-react';

export const industries = [
  {
    id: 'customer-support',
    label: 'Customer Support',
    icon: Headphones,
    tagline: 'Resolve 80%+ of queries without a human agent',
    agents: [
      { name: 'Multi-Channel Support Agent', desc: 'Handles WhatsApp, email, and live chat from a single agent — no channel silos', type: 'conversational' },
      { name: 'Email Triage Agent', desc: 'Reads, categorises, and routes every support email to the right team automatically', type: 'workflow' },
      { name: 'FAQ & Knowledge Agent', desc: 'Answers repetitive questions instantly from your product docs and SOPs', type: 'conversational' },
      { name: 'Escalation Agent', desc: 'Detects frustration or complexity, creates CRM cases, and loops in a human at the right moment', type: 'workflow' },
    ],
    outcome: '3,000+ queries/day handled automatically',
    solution: '/solutions/conversational-agents',
  },
  {
    id: 'human-resources',
    label: 'Human Resources',
    icon: Users,
    tagline: 'Automate the admin so HR can focus on people',
    agents: [
      { name: 'Hiring & Screening Agent', desc: 'Pre-screens CVs against job criteria, schedules interviews, and sends rejections automatically', type: 'workflow' },
      { name: 'Employee Onboarding Agent', desc: 'Guides new hires through documents, tools, and first-week tasks step by step', type: 'conversational' },
      { name: 'Leave Management Agent', desc: 'Checks balances, books time off, and notifies managers — via chat', type: 'conversational' },
      { name: 'Performance Review Agent', desc: 'Collects peer feedback, aggregates scores, and surfaces insights for review cycles', type: 'workflow' },
    ],
    outcome: 'Onboarding time cut from 3 days to 4 hours',
    solution: '/solutions/workflow-agents',
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: TrendingUp,
    tagline: 'Qualify more leads and close deals faster',
    agents: [
      { name: 'SDR & Lead Qualification Agent', desc: 'Qualifies inbound leads with discovery questions and books calls with qualified prospects', type: 'conversational' },
      { name: 'CRM Enrichment Agent', desc: 'Auto-fills company data, contact info, and deal context so reps always have full context', type: 'workflow' },
      { name: 'Deal Nurture Agent', desc: 'Sends timely follow-ups, resurfaces stalled deals, and tracks reply engagement', type: 'workflow' },
      { name: 'Proposal Generation Agent', desc: 'Drafts tailored proposals from deal context, product data, and pricing rules', type: 'workflow' },
    ],
    outcome: '40% more qualified meetings booked per rep',
    solution: '/solutions/conversational-agents',
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    tagline: 'Publish more, research faster, stay consistent',
    agents: [
      { name: 'Content Creation Agent', desc: 'Drafts blogs, social posts, and email sequences on your publishing schedule', type: 'workflow' },
      { name: 'SEO & Competitor Agent', desc: 'Tracks keyword rankings and competitor changes daily — report lands in your inbox', type: 'data' },
      { name: 'ABM Campaign Agent', desc: 'Researches target accounts and personalises outreach messages at scale', type: 'workflow' },
      { name: 'Social Media Agent', desc: 'Schedules and publishes content across LinkedIn, Twitter, and Instagram automatically', type: 'workflow' },
    ],
    outcome: '5× content output with the same team size',
    solution: '/solutions/workflow-agents',
  },
  {
    id: 'banking-fintech',
    label: 'Banking & Fintech',
    icon: Landmark,
    tagline: 'Compliance-ready agents for regulated financial workflows',
    agents: [
      { name: 'KYC Compliance Agent', desc: 'Verifies identity documents, runs AML checks, and flags anomalies for review', type: 'enterprise' },
      { name: 'Loan Origination Agent', desc: 'Collects applications, validates data completeness, and routes for underwriting', type: 'workflow' },
      { name: 'Fraud Detection Agent', desc: 'Monitors transactions in real time and surfaces behavioural anomalies instantly', type: 'data' },
      { name: 'Regulatory Monitoring Agent', desc: 'Tracks rule changes across jurisdictions and maps impact to internal policies', type: 'enterprise' },
    ],
    outcome: 'KYC processing time reduced by 70%',
    solution: '/solutions/enterprise',
  },
  {
    id: 'insurance',
    label: 'Insurance',
    icon: Shield,
    tagline: 'Process claims and stay compliant without manual overhead',
    agents: [
      { name: 'Claims Processing Agent', desc: 'Handles intake, validates supporting documents, and routes to the right adjuster', type: 'workflow' },
      { name: 'Policy Underwriting Agent', desc: 'Gathers risk data, scores applications, and flags edge cases for human review', type: 'enterprise' },
      { name: 'Document Extraction Agent', desc: 'Pulls key clauses and figures from policy documents and legal filings at scale', type: 'data' },
      { name: 'Compliance Audit Agent', desc: 'Runs periodic checks against regulatory requirements and generates audit-ready reports', type: 'enterprise' },
    ],
    outcome: 'Claims intake time cut from 2 days to 3 hours',
    solution: '/solutions/enterprise',
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    icon: Heart,
    tagline: 'Reduce admin burden and improve patient experience',
    agents: [
      { name: 'Patient Intake Agent', desc: 'Collects symptoms, history, and insurance info before appointments via chat', type: 'conversational' },
      { name: 'Appointment Scheduling Agent', desc: 'Books, reschedules, and sends reminders automatically across clinics', type: 'conversational' },
      { name: 'Medical Records Agent', desc: 'Extracts and structures data from unstructured clinical notes and discharge summaries', type: 'data' },
      { name: 'Claims Validation Agent', desc: 'Checks billing codes against procedure data and flags submission errors before filing', type: 'workflow' },
    ],
    outcome: '60% reduction in front-desk call volume',
    solution: '/solutions/workflow-agents',
  },
  {
    id: 'logistics',
    label: 'Logistics',
    icon: Truck,
    tagline: 'Move documents, invoices, and data as fast as your freight',
    agents: [
      { name: 'Invoice Processing Agent', desc: 'Extracts line items, validates against POs, and routes supplier invoices for approval', type: 'workflow' },
      { name: 'Shipment Tracking Agent', desc: 'Monitors carrier APIs, surfaces delays, and alerts stakeholders before SLA breach', type: 'data' },
      { name: 'Supplier Sourcing Agent', desc: 'Researches and ranks vendors by capacity, lead time, and price automatically', type: 'data' },
      { name: 'Document Intake Agent', desc: 'Processes customs, compliance, and shipping documents at scale with zero manual entry', type: 'workflow' },
    ],
    outcome: 'Invoice processing: 4 days → 4 hours',
    solution: '/solutions/workflow-agents',
  },
  {
    id: 'ecommerce',
    label: 'E-commerce',
    icon: ShoppingBag,
    tagline: 'Handle every order query and return automatically',
    agents: [
      { name: 'Order Support Agent', desc: 'Handles order queries, tracking updates, and address changes via WhatsApp or web chat', type: 'conversational' },
      { name: 'Returns & Refund Agent', desc: 'Guides customers through returns, triggers refund workflows, and updates inventory', type: 'conversational' },
      { name: 'Product Recommendation Agent', desc: 'Suggests relevant products based on browsing history and purchase patterns', type: 'conversational' },
      { name: 'Inventory Alert Agent', desc: 'Monitors stock levels, triggers reorder notifications, and alerts category managers', type: 'data' },
    ],
    outcome: '80% of order queries resolved without human',
    solution: '/solutions/conversational-agents',
  },
  {
    id: 'legal',
    label: 'Legal',
    icon: Scale,
    tagline: 'Review contracts and monitor compliance at scale',
    agents: [
      { name: 'Contract Review Agent', desc: 'Analyses agreements, flags risky clauses, and surfaces non-standard terms for counsel', type: 'data' },
      { name: 'Document Search Agent', desc: 'Finds relevant precedents and clauses across your entire document library in seconds', type: 'data' },
      { name: 'Clause Extraction Agent', desc: 'Pulls specific provisions (liability, IP, termination) from large contract batches', type: 'workflow' },
      { name: 'Compliance Monitoring Agent', desc: 'Tracks regulatory changes and maps them to internal policies automatically', type: 'enterprise' },
    ],
    outcome: 'Contract review time reduced by 65%',
    solution: '/solutions/data-agents',
  },
];

export const homepageIndustries = industries.filter(ind =>
  ['customer-support', 'human-resources', 'sales', 'banking-fintech', 'logistics', 'ecommerce'].includes(ind.id)
);

export const badgeLabel = {
  conversational: 'Conversational Agent',
  workflow: 'Workflow Agent',
  data: 'Data Agent',
  enterprise: 'Enterprise',
};
